import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  FileText, Plus, Calendar, AlertTriangle, CheckCircle, Clock,
  Edit, Trash2, Loader2, Search, User, DollarSign, RefreshCw, History, ChevronRight, Paperclip, Download, Eye
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import AttachmentPreviewModal from '../components/AttachmentPreviewModal';

const CONTRACT_TYPES = ['Employment', 'Partners', 'Service', 'Vendor', 'Lease', 'Subscriptions', 'Consultancy'];
const CONTRACT_STATUSES = ['Draft', 'Active', 'Expired', 'Terminated', 'Renewed'];

const ContractLedger = () => {
  const [contracts, setContracts] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editContract, setEditContract] = useState(null);
  const [renewContract, setRenewContract] = useState(null);
  const [contractHistory, setContractHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [formData, setFormData] = useState({
    title: '', contract_type: 'Service', party_name: '',
    start_date: '', end_date: '', contract_value: '', currency: 'TZS',
    alert_days_before_expiry: 30, notice_to_terminate_days: '', highlights: ''
  });
  const [renewalData, setRenewalData] = useState({
    new_start_date: '', new_end_date: '', new_contract_value: '',
    new_highlights: '', notice_to_terminate_days: '', alert_days_before_expiry: 30
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [previewContract, setPreviewContract] = useState(null);
  const fileInputRef = React.useRef(null);

  const { getAuthHeaders, currentOffice, isSuperAdmin } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchContracts = async () => {
    try {
      const headers = getAuthHeaders();
      // For super admin: do NOT scope by currentOffice — always show cross-office data
      // For office admin: getAuthHeaders() already injects X-Office-ID via AuthContext
      if (currentOffice?.id && !isSuperAdmin()) headers['X-Office-ID'] = currentOffice.id;
      
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (typeFilter !== 'all') params.append('contract_type', typeFilter);
      
      const response = await fetch(`${API_URL}/api/contracts/?${params}`, { headers });
      if (response.ok) {
        const allContracts = await response.json();
        // Filter out contracts that have been renewed (status = 'Renewed')
        // These are old contracts superseded by new ones, accessible via history
        const currentContracts = allContracts.filter(c => c.status !== 'Renewed');
        setContracts(currentContracts);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchExpiring = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id && !isSuperAdmin()) headers['X-Office-ID'] = currentOffice.id;
      const response = await fetch(`${API_URL}/api/contracts/expiring?days=30`, { headers });
      if (response.ok) setExpiringContracts(await response.json());
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchContracts(), fetchExpiring()]);
      setLoading(false);
    };
    // For super admin with global access, we still need to load data
    loadData();
  }, [currentOffice, searchQuery, typeFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const url = editContract 
        ? `${API_URL}/api/contracts/${editContract.id}`
        : `${API_URL}/api/contracts/`;
      
      const payload = {
          ...formData,
          office_id: currentOffice.id,
          contract_value: formData.contract_value ? parseFloat(formData.contract_value) : null,
          notice_to_terminate_days: formData.notice_to_terminate_days ? parseInt(formData.notice_to_terminate_days) : null,
          highlights: formData.highlights || null
        };

      // Include PDF attachment if uploaded
      if (attachmentFile) {
        const reader = new FileReader();
        const base64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(attachmentFile);
        });
        payload.attachment_pdf = base64;
        payload.attachment_filename = attachmentFile.name;
      }

      const response = await fetch(url, {
        method: editContract ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowModal(false);
        setEditContract(null);
        setAttachmentFile(null);
        resetForm();
        await Promise.all([fetchContracts(), fetchExpiring()]);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Operation failed');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contract?')) return;
    try {
      await fetch(`${API_URL}/api/contracts/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      await fetchContracts();
      toast.success('Contract deleted');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete contract');
    }
  };

  const handleDownloadAttachment = async (contractId, contractNumber) => {
    setDownloadingPdf(contractId);
    try {
      const response = await fetch(`${API_URL}/api/contracts/${contractId}/attachment`, { headers: getAuthHeaders() });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Contract_${contractNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Contract PDF downloaded');
      } else {
        toast.error('Failed to download');
      }
    } catch (error) {
      toast.error('Failed to download');
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Renewal functions
  const openRenewModal = (contract) => {
    if (contract.status !== 'Active') {
      toast.error(`Only Active contracts can be renewed. Current status: ${contract.status}`);
      return;
    }
    if (contract.renewed_to_id) {
      toast.error('This contract has already been renewed');
      return;
    }
    setRenewContract(contract);
    // Pre-fill with next day after current end date
    const currentEndDate = new Date(contract.end_date);
    const newStartDate = new Date(currentEndDate);
    newStartDate.setDate(newStartDate.getDate() + 1);
    
    setRenewalData({
      new_start_date: newStartDate.toISOString().split('T')[0],
      new_end_date: '',
      new_contract_value: contract.contract_value || '',
      new_highlights: contract.highlights || '',
      notice_to_terminate_days: contract.notice_to_terminate_days || '',
      alert_days_before_expiry: contract.alert_days_before_expiry || 30
    });
    setShowRenewModal(true);
  };

  const handleRenewal = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;

      const response = await fetch(`${API_URL}/api/contracts/${renewContract.id}/renew`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          new_start_date: renewalData.new_start_date,
          new_end_date: renewalData.new_end_date,
          new_contract_value: renewalData.new_contract_value ? parseFloat(renewalData.new_contract_value) : null,
          new_highlights: renewalData.new_highlights || null,
          notice_to_terminate_days: renewalData.notice_to_terminate_days ? parseInt(renewalData.notice_to_terminate_days) : null,
          alert_days_before_expiry: parseInt(renewalData.alert_days_before_expiry) || 30
        })
      });

      if (response.ok) {
        const newContract = await response.json();
        setShowRenewModal(false);
        setRenewContract(null);
        await Promise.all([fetchContracts(), fetchExpiring()]);
        toast.success(`Contract renewed! New contract number: ${newContract.contract_number}`);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Renewal failed');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  // History functions
  const openHistoryModal = async (contract) => {
    setHistoryLoading(true);
    setShowHistoryModal(true);
    setContractHistory([]);

    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;

      const response = await fetch(`${API_URL}/api/contracts/${contract.id}/history`, { headers });
      if (response.ok) {
        const history = await response.json();
        setContractHistory(history);
      } else {
        toast.error('Failed to load contract history');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load contract history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openEditModal = (contract) => {
    setEditContract(contract);
    setFormData({
      title: contract.title,
      contract_type: contract.contract_type,
      party_name: contract.party_name,
      start_date: contract.start_date,
      end_date: contract.end_date,
      contract_value: contract.contract_value || '',
      currency: contract.currency,
      alert_days_before_expiry: contract.alert_days_before_expiry,
      notice_to_terminate_days: contract.notice_to_terminate_days || '',
      highlights: contract.highlights || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '', contract_type: 'Service', party_name: '',
      start_date: '', end_date: '', contract_value: '', currency: 'TZS',
      alert_days_before_expiry: 30, notice_to_terminate_days: '', highlights: ''
    });
  };

  const openNewContractModal = () => {
    resetForm();
    setEditContract(null);
    setShowModal(true);
  };

  const getStatusBadge = (status, daysUntil) => {
    if (daysUntil !== undefined && daysUntil <= 0) {
      return <Badge className="bg-red-100 text-red-800">Expired</Badge>;
    }
    if (daysUntil !== undefined && daysUntil <= 30) {
      return <Badge className="bg-yellow-100 text-yellow-800">Expiring Soon</Badge>;
    }
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Active': 'bg-green-100 text-green-800',
      'Expired': 'bg-red-100 text-red-800',
      'Terminated': 'bg-red-100 text-red-800',
      'Renewed': 'bg-blue-100 text-blue-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Contract Ledger</h1>
          <p className="text-gray-600">Manage contracts and track expiry dates</p>
        </div>
        <Button onClick={openNewContractModal} data-testid="add-contract-button">
          <Plus className="h-4 w-4 mr-2" />
          New Contract
        </Button>
      </div>

      {/* Expiring Soon Alert */}
      {expiringContracts.length > 0 && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <span className="font-semibold">{expiringContracts.length} contract(s)</span> expiring within 30 days
          </AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg"><FileText className="h-6 w-6 text-blue-600" /></div>
              <div><p className="text-sm text-gray-600">Total Contracts</p><p className="text-2xl font-bold">{contracts.length}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-lg"><CheckCircle className="h-6 w-6 text-green-600" /></div>
              <div><p className="text-sm text-gray-600">Active</p><p className="text-2xl font-bold">{contracts.filter(c => c.status === 'Active').length}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-yellow-100 rounded-lg"><AlertTriangle className="h-6 w-6 text-yellow-600" /></div>
              <div><p className="text-sm text-gray-600">Expiring Soon</p><p className="text-2xl font-bold">{expiringContracts.length}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-red-100 rounded-lg"><Clock className="h-6 w-6 text-red-600" /></div>
              <div><p className="text-sm text-gray-600">Expired</p><p className="text-2xl font-bold">{contracts.filter(c => c.days_until_expiry <= 0).length}</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search contracts..." className="pl-9" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {CONTRACT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contract List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {contracts.map(contract => (
          <Card key={contract.id} className={`hover:shadow-md transition-shadow ${contract.days_until_expiry <= 0 ? 'border-red-200' : contract.is_expiring_soon ? 'border-yellow-200' : ''}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-sm text-gray-500">{contract.contract_number}</p>
                  <h3 className="font-semibold">{contract.title}</h3>
                </div>
                {getStatusBadge(contract.status, contract.days_until_expiry)}
              </div>

              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="h-4 w-4" />
                  <span>{contract.party_name}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(contract.start_date).toLocaleDateString()} - {new Date(contract.end_date).toLocaleDateString()}</span>
                </div>
                {contract.days_until_expiry > 0 && contract.status === 'Active' && (
                  <p className="text-xs text-gray-500 ml-6">{contract.days_until_expiry} days remaining</p>
                )}
                {contract.contract_value && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <DollarSign className="h-4 w-4" />
                    <span>{contract.currency} {contract.contract_value?.toLocaleString()}</span>
                  </div>
                )}
                {contract.notice_to_terminate_days && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span>{contract.notice_to_terminate_days} days notice to terminate</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">{contract.contract_type}</Badge>
                  {contract.renewal_number > 0 && (
                    <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                      Renewal #{contract.renewal_number}
                    </Badge>
                  )}
                  {contract.has_attachment && (
                    <Badge variant="outline" className="text-xs text-orange-600 border-orange-200">
                      <Paperclip className="h-3 w-3 mr-0.5" />PDF
                    </Badge>
                  )}
                </div>
              </div>

              {contract.highlights && (
                <div className="mb-4 p-2 bg-blue-50 rounded-md">
                  <p className="text-xs text-gray-600 font-medium mb-1">Highlights:</p>
                  <p className="text-xs text-gray-700 line-clamp-2">{contract.highlights}</p>
                </div>
              )}

              {/* Renewed contract banner - shows on contracts that are renewals */}
              {contract.parent_contract_id && (
                <div className="mb-4 p-2 bg-green-100 border border-green-200 rounded-md flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 text-green-600" />
                    <p className="text-sm text-green-700 font-medium">This contract is renewed</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-green-700 h-6 px-2"
                    onClick={() => openHistoryModal(contract)}
                  >
                    <History className="h-3 w-3 mr-1" /> View Previous
                  </Button>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => openEditModal(contract)}>
                  <Edit className="h-4 w-4 mr-1" />Edit
                </Button>
                {contract.has_attachment && (
                  <>
                    <Button
                      variant="outline" size="sm"
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      onClick={() => setPreviewContract(contract)}
                      data-testid="preview-contract-pdf"
                    >
                      <Eye className="h-4 w-4 mr-1" /> Preview
                    </Button>
                    <Button 
                      variant="outline" size="sm"
                      className="text-orange-600 border-orange-200 hover:bg-orange-50"
                      onClick={() => handleDownloadAttachment(contract.id, contract.contract_number)}
                      disabled={downloadingPdf === contract.id}
                      data-testid="download-contract-pdf"
                    >
                      {downloadingPdf === contract.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                      PDF
                    </Button>
                  </>
                )}
                {contract.status === 'Active' && !contract.renewed_to_id && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-green-600 border-green-200 hover:bg-green-50"
                    onClick={() => openRenewModal(contract)}
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />Renew
                  </Button>
                )}
                {contract.parent_contract_id && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                    onClick={() => openHistoryModal(contract)}
                  >
                    <History className="h-4 w-4 mr-1" />History
                  </Button>
                )}
                <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(contract.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {contracts.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No contracts yet</h3>
            <Button onClick={openNewContractModal}><Plus className="h-4 w-4 mr-2" />New Contract</Button>
          </CardContent>
        </Card>
      )}

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contract-modal">
          <DialogHeader>
            <DialogTitle>{editContract ? `Edit Contract: ${editContract.contract_number}` : 'New Contract'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            
            {!editContract && (
              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription className="text-blue-700">
                  Contract number will be auto-generated based on start date when saved.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required placeholder="Contract title" />
              </div>
              <div className="space-y-2">
                <Label>Contract Type *</Label>
                <Select value={formData.contract_type} onValueChange={(v) => setFormData({...formData, contract_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTRACT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Party Name *</Label>
              <Input value={formData.party_name} onChange={(e) => setFormData({...formData, party_name: e.target.value})} required placeholder="Other party in the contract" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} required disabled={!!editContract} />
                {!editContract && <p className="text-xs text-gray-500">Used in contract number generation</p>}
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contract Value</Label>
                <Input type="number" value={formData.contract_value} onChange={(e) => setFormData({...formData, contract_value: e.target.value})} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>Notice to Terminate (Days)</Label>
                <Input 
                  type="number" 
                  value={formData.notice_to_terminate_days} 
                  onChange={(e) => setFormData({...formData, notice_to_terminate_days: e.target.value})} 
                  placeholder="e.g., 30, 60, 90"
                />
                <p className="text-xs text-gray-500">Days notice required to terminate</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Contract Highlights</Label>
              <Textarea 
                value={formData.highlights} 
                onChange={(e) => setFormData({...formData, highlights: e.target.value})} 
                placeholder="Key points, important clauses, or summary of the contract (optional)"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Alert Days Before Expiry</Label>
              <Input type="number" value={formData.alert_days_before_expiry} onChange={(e) => setFormData({...formData, alert_days_before_expiry: parseInt(e.target.value) || 30})} />
              <p className="text-xs text-gray-500">Get notified this many days before contract expires</p>
            </div>

            <div className="space-y-2">
              <Label>Contract PDF</Label>
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  data-testid="contract-pdf-upload"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 10 * 1024 * 1024) {
                        toast.error('File size must be under 10MB');
                        return;
                      }
                      setAttachmentFile(file);
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2"
                  data-testid="contract-pdf-upload-btn"
                >
                  <Paperclip className="h-4 w-4" />
                  {attachmentFile ? 'Change File' : 'Upload PDF'}
                </Button>
                {attachmentFile && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FileText className="h-4 w-4 text-red-500" />
                    <span className="truncate max-w-[200px]">{attachmentFile.name}</span>
                    <button type="button" onClick={() => { setAttachmentFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-gray-400 hover:text-red-500">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                )}
                {!attachmentFile && editContract?.has_attachment && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <Paperclip className="h-3 w-3" /> Existing PDF attached
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">PDF only, max 10MB</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editContract ? 'Update' : 'Create Contract'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Renewal Modal */}
      <Dialog open={showRenewModal} onOpenChange={setShowRenewModal}>
        <DialogContent className="max-w-lg" data-testid="renewal-modal">
          <DialogHeader>
            <DialogTitle>Renew Contract</DialogTitle>
          </DialogHeader>
          {renewContract && (
            <form onSubmit={handleRenewal} className="space-y-4">
              {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
              
              {/* Current contract info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Renewing: {renewContract.contract_number}</p>
                <p className="text-sm text-gray-600">{renewContract.title}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Current period: {new Date(renewContract.start_date).toLocaleDateString()} - {new Date(renewContract.end_date).toLocaleDateString()}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>New Start Date *</Label>
                  <Input 
                    type="date" 
                    value={renewalData.new_start_date} 
                    onChange={(e) => setRenewalData({...renewalData, new_start_date: e.target.value})} 
                    required 
                  />
                  <p className="text-xs text-gray-500">Used in new contract number</p>
                </div>
                <div className="space-y-2">
                  <Label>New End Date *</Label>
                  <Input 
                    type="date" 
                    value={renewalData.new_end_date} 
                    onChange={(e) => setRenewalData({...renewalData, new_end_date: e.target.value})} 
                    required 
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>New Contract Value</Label>
                  <Input 
                    type="number" 
                    value={renewalData.new_contract_value} 
                    onChange={(e) => setRenewalData({...renewalData, new_contract_value: e.target.value})} 
                    placeholder={renewContract.contract_value?.toString() || "0.00"}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notice to Terminate (Days)</Label>
                  <Input 
                    type="number" 
                    value={renewalData.notice_to_terminate_days} 
                    onChange={(e) => setRenewalData({...renewalData, notice_to_terminate_days: e.target.value})} 
                    placeholder={renewContract.notice_to_terminate_days?.toString() || "30"}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Updated Highlights</Label>
                <Textarea 
                  value={renewalData.new_highlights} 
                  onChange={(e) => setRenewalData({...renewalData, new_highlights: e.target.value})} 
                  placeholder="Update key points for the renewed contract (optional)"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Alert Days Before Expiry</Label>
                <Input 
                  type="number" 
                  value={renewalData.alert_days_before_expiry} 
                  onChange={(e) => setRenewalData({...renewalData, alert_days_before_expiry: parseInt(e.target.value) || 30})} 
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowRenewModal(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} className="bg-green-600 hover:bg-green-700">
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Renew Contract
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-2xl" data-testid="history-modal">
          <DialogHeader>
            <DialogTitle>Contract History</DialogTitle>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4 pr-4">
                {contractHistory.map((contract, index) => (
                  <div 
                    key={contract.id} 
                    className={`p-4 rounded-lg border ${contract.status === 'Active' ? 'border-green-300 bg-green-50' : contract.status === 'Renewed' ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium">{contract.contract_number}</span>
                          {contract.renewal_number > 0 && (
                            <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                              Renewal #{contract.renewal_number}
                            </Badge>
                          )}
                          {contract.renewal_number === 0 && (
                            <Badge variant="secondary" className="text-xs">Original</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{contract.title}</p>
                      </div>
                      {getStatusBadge(contract.status, contract.days_until_expiry)}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <p className="text-xs text-gray-500">Period</p>
                        <p>{new Date(contract.start_date).toLocaleDateString()} - {new Date(contract.end_date).toLocaleDateString()}</p>
                      </div>
                      {contract.contract_value && (
                        <div>
                          <p className="text-xs text-gray-500">Value</p>
                          <p>{contract.currency} {contract.contract_value?.toLocaleString()}</p>
                        </div>
                      )}
                    </div>

                    {contract.highlights && (
                      <div className="mt-2 text-sm">
                        <p className="text-xs text-gray-500">Highlights</p>
                        <p className="text-gray-700">{contract.highlights}</p>
                      </div>
                    )}

                    {/* Arrow to next contract */}
                    {index < contractHistory.length - 1 && (
                      <div className="flex justify-center mt-4">
                        <div className="flex flex-col items-center text-gray-400">
                          <span className="text-xs">Renewed to</span>
                          <ChevronRight className="h-4 w-4 rotate-90" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AttachmentPreviewModal
        open={!!previewContract}
        onClose={() => setPreviewContract(null)}
        title={`Contract ${previewContract?.contract_number || ''} - ${previewContract?.title || ''}`}
        downloadName={`Contract_${previewContract?.contract_number || previewContract?.id}.pdf`}
        fetchUrl={previewContract ? `${API_URL}/api/contracts/${previewContract.id}/attachment` : ''}
        headers={getAuthHeaders()}
      />
    </div>
  );
};

export default ContractLedger;
